import { useState } from "react";
import api, { apiError, maskDoc } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, X } from "lucide-react";
import { Modal, ConfirmDialog } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Badge, Button, DataTable, Field, FilterSheet, Input, PageHeader, SearchInput, Select, Textarea } from "@/components/ui-kit";

const EMPTY = {
  name: "", cnpj: "", ie: "", email: "", phone: "", contact_name: "",
  cep: "", street: "", number: "", complement: "", district: "", city: "", state: "", notes: "",
};

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];


/** Atalho local: liga um campo do kit direto a uma chave do formulário. */
function Fld({ label, k, form, setForm, span = 1, mask }) {
  return (
    <Field label={label} className={span === 2 ? "md:col-span-2" : undefined}>
      <Input
        value={form[k] ?? ""}
        onChange={(e) => setForm({ ...form, [k]: mask ? mask(e.target.value) : e.target.value })}
        data-testid={`supplier-form-${k}`}
      />
    </Field>
  );
}

function SupplierForm({ supplier, onClose, onSaved }) {
  const [form, setForm] = useState(supplier ? { ...EMPTY, ...supplier } : EMPTY);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome do fornecedor"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (supplier?.id) await api.patch(`/admin/suppliers/${supplier.id}`, payload);
      else await api.post("/admin/suppliers", payload);
      toast.success(supplier ? "Fornecedor atualizado" : "Fornecedor criado");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={supplier ? "Editar fornecedor" : "Novo fornecedor"} maxWidth="max-w-2xl" testId="supplier-form-dialog">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Fld label="Nome / Razão social" k="name" form={form} setForm={setForm} span={2} />
        <Fld label="CNPJ" k="cnpj" form={form} setForm={setForm} mask={maskDoc} />
        <Fld label="Inscrição Estadual" k="ie" form={form} setForm={setForm} />
        <Fld label="E-mail" k="email" form={form} setForm={setForm} />
        <Fld label="Telefone" k="phone" form={form} setForm={setForm} />
        <Fld label="Contato (pessoa)" k="contact_name" form={form} setForm={setForm} span={2} />
        <Fld label="CEP" k="cep" form={form} setForm={setForm} />
        <Fld label="Cidade" k="city" form={form} setForm={setForm} />
        <Fld label="Rua" k="street" form={form} setForm={setForm} span={2} />
        <Fld label="Número" k="number" form={form} setForm={setForm} />
        <Fld label="Bairro" k="district" form={form} setForm={setForm} />
        <Fld label="Complemento" k="complement" form={form} setForm={setForm} />
        <Fld label="UF" k="state" form={form} setForm={setForm} />
        <Field label="Observações" className="md:col-span-2">
          <Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="supplier-form-notes" />
        </Field>
      </div>
      <Button onClick={save} loading={saving} size="lg" block className="mt-4" data-testid="supplier-form-save">
        {saving ? "Salvando..." : "Salvar"}
      </Button>
    </Modal>
  );
}

export default function Suppliers() {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [uf, setUf] = useState("");
  const [situacao, setSituacao] = useState("ativos");

  // Todos os filtros vão para o servidor: filtrar o array da página em memória
  // deixaria páginas curtas e o total contando linhas que a tela não mostra.
  const {
    items: suppliers, total, page, lastPage, setPage, search, setSearch, sort, setSort,
    isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/admin/suppliers", { situacao, uf });

  // "ativos"/"nome" são os padrões, então não contam como filtro aplicado.
  const hasFilters = !!search || !!uf || situacao !== "ativos";
  const clearFilters = () => { setSearch(""); setUf(""); setSituacao("ativos"); setSort("nome"); };

  const remove = async (s) => {
    try { await api.delete(`/admin/suppliers/${s.id}`); toast.success("Fornecedor desativado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Mesmos controles para a linha (desktop) e para a folha (mobile).
  const filtros = (
    <>
            <Select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-testid="supplier-situacao-filter"
              aria-label="Filtrar por situação" className="w-auto px-2.5 text-slate-600">
              <option value="ativos">Ativos</option>
              <option value="inativos">Inativos</option>
              <option value="todos">Todas as situações</option>
            </Select>
            <Select value={uf} onChange={(e) => setUf(e.target.value)} data-testid="supplier-uf-filter"
              aria-label="Filtrar por UF" className="w-auto px-2.5 text-slate-600">
              <option value="">Todas as UFs</option>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
            {hasFilters && (
              <Button variant="link" size="sm" icon={X} onClick={clearFilters} data-testid="clear-filters-btn">Limpar filtros</Button>
            )}
          </>

  );
  // "ativos"/"nome" são os padrões: não contam como filtro aplicado.
  const filtrosAtivos = [uf, situacao !== "ativos" ? situacao : ""].filter(Boolean).length;

  return (
    <div className="space-y-5" data-testid="admin-suppliers-page">
      <PageHeader
        eyebrow="Operação"
        title="Fornecedores"
        icon={Truck}
        description="Quem abastece o estoque. O vínculo com o fornecedor alimenta a importação de XML e o filtro do catálogo."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, CNPJ, e-mail, contato ou cidade" data-testid="supplier-search-input" aria-label="Buscar fornecedor por nome, CNPJ, e-mail, contato ou cidade" width="w-72" />
            <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="supplier-new-btn">
              Novo fornecedor
            </Button>
          </>
        )}
        filters={filtros}
        mobileFilters={<FilterSheet count={filtrosAtivos} onClear={clearFilters}>{filtros}</FilterSheet>}
      />

      <DataTable
        caption="Fornecedores cadastrados"
        rows={suppliers}
        sort={sort}
        onSortChange={setSort}
        rowKey={(s) => s.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: Truck,
          title: hasFilters ? "Nenhum fornecedor encontrado" : "Nenhum fornecedor",
          desc: hasFilters
            ? "Tente outro termo de busca ou limpe os filtros."
            : "Cadastre fornecedores para vincular a produtos e importações de estoque.",
          action: hasFilters
            ? <Button variant="secondary" onClick={clearFilters}>Limpar filtros</Button>
            : <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>Cadastrar fornecedor</Button>,
          testId: "supplier-empty",
        }}
        columns={[
          {
            key: "fornecedor", header: "Fornecedor", sortable: true,
            cell: (s) => (
              <>
                <p className="font-medium text-slate-900 flex items-center gap-2">
                  {s.name}
                  {/* Com "inativos"/"todos" na tela, sem o selo não dá para saber qual está desativado. */}
                  {!s.active && <Badge data-testid="supplier-inactive-badge">Inativo</Badge>}
                </p>
                {s.email && <p className="text-xs text-slate-400">{s.email}</p>}
              </>
            ),
          },
          { key: "cnpj", header: "CNPJ", mono: true, sortable: true, className: "text-xs text-slate-500", cell: (s) => s.cnpj || "—" },
          { key: "contato", header: "Contato", sortable: true, className: "text-slate-600", cell: (s) => `${s.contact_name || "—"}${s.phone ? ` · ${s.phone}` : ""}` },
          { key: "cidade", header: "Cidade/UF", sortable: true, className: "text-slate-600", cell: (s) => [s.city, s.state].filter(Boolean).join("/") || "—" },
          {
            key: "acoes", header: "Ações", align: "right", className: "whitespace-nowrap",
            cell: (s) => (
              <>
                <Button variant="ghost" size="iconSm" onClick={() => { setEditing(s); setShowForm(true); }}
                  data-testid="supplier-edit-btn" aria-label={`Editar ${s.name}`} className="hover:text-brand-600"><Pencil size={16} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => setDeleting(s)}
                  data-testid="supplier-delete-btn" aria-label={`Desativar ${s.name}`} className="hover:text-red-600"><Trash2 size={16} /></Button>
              </>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="fornecedores" />

      {showForm && <SupplierForm supplier={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={() => remove(deleting)}
        title="Desativar fornecedor" message={`Desativar o fornecedor "${deleting?.name}"?`} confirmLabel="Desativar" />
    </div>
  );
}
